import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

// Import util modules
import { FormattedMessage } from '../../util/reactIntl';

// Import shared components
import { Modal, Button } from '../../components';

// Import modules from this directory
import css from './ServiceSelectionModal.module.css';

const formatServiceLabel = service => {
  if (!service) return '';
  const words = service
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[-_]/g, ' ')
    .trim()
    .split(/\s+/);
  return words
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * ServiceSelectionModal component displays a modal with checkboxes
 * to select a single service from the available services needed for a tour.
 */
const ServiceSelectionModal = props => {
  const {
    id,
    isOpen,
    onClose,
    onManageDisableScrolling,
    services,
    onSelectService,
  } = props;

  const [selectedService, setSelectedService] = useState(null);

  const handleCheckboxChange = service => {
    setSelectedService(service);
  };

  const handleConfirm = () => {
    if (selectedService) {
      onSelectService(selectedService);
    }
  };

  return (
    <Modal
      id={id}
      isOpen={isOpen}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling}
      containerClassName={css.modalContainer}
      usePortal
    >
      <div className={css.root}>
        <h2 className={css.title}>
          <FormattedMessage id="ServiceSelectionModal.title" />
        </h2>

        <p className={css.description}>
          <FormattedMessage id="ServiceSelectionModal.description" />
        </p>

        {services.length === 0 ? (
          <div className={css.noServices}>
            <p>
              <FormattedMessage id="ServiceSelectionModal.noServices" />
            </p>
          </div>
        ) : (
          <div className={css.servicesList}>
            {services.map(service => {
              const isSelected = selectedService === service;
              const cleanKey = (service || '').toLowerCase().trim();
              const fallbackLabel = formatServiceLabel(service);

              return (
                <label
                  key={service}
                  className={classNames(css.serviceItem, {
                    [css.selected]: isSelected,
                  })}
                  htmlFor={`service-${service}`}
                >
                  <input
                    id={`service-${service}`}
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleCheckboxChange(service)}
                    className={css.checkbox}
                  />
                  <span className={css.serviceLabel}>
                    <FormattedMessage
                      id={`ServiceSelectionModal.service.${cleanKey}`}
                      defaultMessage={fallbackLabel}
                    />
                  </span>
                </label>
              );
            })}
          </div>
        )}

        <div className={css.actions}>
          <Button rootClassName={css.cancelButton} onClick={onClose}>
            <FormattedMessage id="ServiceSelectionModal.cancel" />
          </Button>
          <Button
            rootClassName={css.confirmButton}
            onClick={handleConfirm}
            disabled={!selectedService}
          >
            <FormattedMessage id="ServiceSelectionModal.confirm" />
          </Button>
        </div>
      </div>
    </Modal>
  );
};

ServiceSelectionModal.defaultProps = {
  services: [],
};

ServiceSelectionModal.propTypes = {
  id: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onManageDisableScrolling: PropTypes.func.isRequired,
  services: PropTypes.array,
  onSelectService: PropTypes.func.isRequired,
};

export default ServiceSelectionModal;
